import { ChangeEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { controllers } from "../../../Controllers/Controllers";
import { STATUS_LABELS } from "../../../DataTypes/leads";
import { MyDashboardDataType } from "../../../DataTypes/dashboard";
import { WorkerUserType } from "../../../DataTypes/activity";
import './my-dashboard-styles.css'

const daysAgoISO = (n: number) => {
    const d = new Date()
    d.setDate(d.getDate() - n)
    return d.toISOString().slice(0, 10)
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const formatPct = (rate: number) => `${Math.round(rate * 100)}%`

const formatDateShort = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

const timeAgo = (isoString: string) => {
    const diffMs = Date.now() - new Date(isoString).getTime()
    const mins = Math.floor(diffMs / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    return new Date(isoString).toLocaleDateString()
}

const CHART_W = 600
const CHART_H = 130
const CHART_PAD_X = 2
const CHART_PAD_Y = 8

const computePoints = (values: number[], maxValue: number) => {
    const n = values.length
    if (n === 0) return []
    const usableW = CHART_W - CHART_PAD_X * 2
    const usableH = CHART_H - CHART_PAD_Y * 2
    const stepX = n > 1 ? usableW / (n - 1) : 0
    return values.map((v, i) => ({
        x: CHART_PAD_X + stepX * i,
        y: CHART_PAD_Y + usableH - (maxValue > 0 ? (v / maxValue) * usableH : 0),
    }))
}

const pointsToPath = (points: { x: number; y: number }[]) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

type MyDashboardState = {
    data: MyDashboardDataType | null;
    workers: WorkerUserType[];
    selectedUserId: string;
    startDate: string;
    endDate: string;
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    hoveredDayIndex: number | null;
}

class MyDashboardPage extends ViewItem {
    state: MyDashboardState;
    gradientId: string;

    constructor(props: any) {
        super(props)
        this.state = {
            data: null,
            workers: [],
            selectedUserId: '',
            startDate: daysAgoISO(30),
            endDate: todayISO(),
            loading: true,
            error: null,
            needsAuth: false,
            hoveredDayIndex: null,
        }
        this.gradientId = `dash-sent-fill-${Math.random().toString(36).slice(2, 9)}`
    }

    componentDidMount(): void {
        this.loadDashboard()
        if (controllers().auth.isAdmin) {
            this.loadWorkers()
        }
    }

    loadWorkers = async () => {
        const response = await controllers().outreach.getWorkers()
        if (response && response.statusCode === 200) {
            this.setState({ workers: response.data?.workers || [] })
        }
    }

    loadDashboard = async () => {
        const { startDate, endDate, selectedUserId } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().outreach.getMyDashboard({
                start_date: startDate,
                end_date: endDate,
                user_id: selectedUserId ? Number(selectedUserId) : undefined,
            })
            if (!response || response.statusCode !== 200) {
                this.setState({ loading: false, error: response?.error || 'Failed to load dashboard.' })
                return
            }
            this.setState({ data: response.data || null, loading: false })
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load dashboard.' })
        }
    }

    handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ startDate: e.target.value }, this.loadDashboard)
    }

    handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ endDate: e.target.value }, this.loadDashboard)
    }

    handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedUserId: e.target.value }, this.loadDashboard)
    }

    handleTrendPointerMove = (clientX: number, containerRect: DOMRect, dayCount: number) => {
        if (dayCount === 0) return
        const relativeX = (clientX - containerRect.left) / containerRect.width
        const index = Math.round(relativeX * (dayCount - 1))
        const clamped = Math.max(0, Math.min(dayCount - 1, index))
        this.setState({ hoveredDayIndex: clamped })
    }

    handleTrendPointerLeave = () => {
        this.setState({ hoveredDayIndex: null })
    }

    renderHero = (data: MyDashboardDataType) => {
        const { funnel } = data
        const reference = Math.max(funnel.contacted, funnel.replied, funnel.converted, 1)
        const stages: { key: string; label: string; count: number }[] = [
            { key: 'contacted', label: 'Contacted', count: funnel.contacted },
            { key: 'replied', label: 'Replied', count: funnel.replied },
            { key: 'converted', label: 'Converted', count: funnel.converted },
        ]

        return (
            <div className="dash-hero">
                <div className="dash-hero-top">
                    <div className="dash-hero-metric">
                        <span className="dash-hero-number">{funnel.total_leads}</span>
                        <span className="dash-hero-label">leads worked this period</span>
                    </div>
                    <div className="dash-hero-rates">
                        <div className="dash-hero-rate">
                            <span className="dash-hero-rate-value">{formatPct(funnel.contact_rate)}</span>
                            <span className="dash-hero-rate-label">contact rate</span>
                        </div>
                        <span className="dash-hero-divider" />
                        <div className="dash-hero-rate">
                            <span className="dash-hero-rate-value">{formatPct(funnel.reply_rate)}</span>
                            <span className="dash-hero-rate-label">reply rate</span>
                        </div>
                        <span className="dash-hero-divider" />
                        <div className="dash-hero-rate">
                            <span className="dash-hero-rate-value">{formatPct(funnel.conversion_rate)}</span>
                            <span className="dash-hero-rate-label">conversion rate</span>
                        </div>
                    </div>
                </div>

                <div className="dash-funnel-flow">
                    {stages.map((stage, i) => {
                        const width = Math.max(16, (stage.count / reference) * 100)
                        return (
                            <div
                                key={stage.key}
                                className={`dash-funnel-seg dash-funnel-seg-${stage.key} ${i === 0 ? 'dash-funnel-seg-first' : ''} ${i === stages.length - 1 ? 'dash-funnel-seg-last' : ''}`}
                                style={{ width: `${width}%` }}
                            >
                                <span className="dash-funnel-count">{stage.count}</span>
                                <span className="dash-funnel-label">{stage.label}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    renderQueue = (data: MyDashboardDataType) => {
        return (
            <div className="dash-panel dash-panel-queue">
                <h3 className="dash-panel-title">Needs your attention</h3>
                {data.action_queue.length === 0 ? (
                    <div className="dash-empty">Nothing urgent right now.</div>
                ) : (
                    <div className="dash-queue-list">
                        {data.action_queue.map(item => (
                            <Link
                                key={item.id}
                                to={`/outreach/${item.id}/edit`}
                                className={`dash-queue-row dash-queue-row-${item.status}`}
                            >
                                <div className="dash-queue-main">
                                    <span className="dash-queue-name">{item.company_name}</span>
                                    {item.decision_maker_name && (
                                        <span className="dash-queue-dm">{item.decision_maker_name}</span>
                                    )}
                                </div>
                                <div className="dash-queue-meta">
                                    <span className={`dash-status-pill dash-status-${item.status}`}>
                                        {STATUS_LABELS[item.status] || item.status}
                                    </span>
                                    <span className="dash-priority-dots" title={`Priority ${item.priority} of 5`}>
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <span key={i} className={`dash-priority-dot ${i < item.priority ? 'dash-priority-dot-filled' : ''}`} />
                                        ))}
                                    </span>
                                    <span className="dash-queue-updated">{timeAgo(item.updated_at)}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    renderPipeline = (data: MyDashboardDataType) => {
        const maxCount = Math.max(1, ...data.pipeline.map(p => p.count))
        return (
            <div className="dash-panel dash-panel-pipeline">
                <h3 className="dash-panel-title">Pipeline</h3>
                <div className="dash-pipeline-list">
                    {data.pipeline.map(p => (
                        <div key={p.status} className="dash-pipeline-row">
                            <span className={`dash-pipeline-swatch dash-status-bg-${p.status}`} />
                            <span className="dash-pipeline-label">{p.label}</span>
                            <div className="dash-pipeline-bar-track">
                                <div
                                    className={`dash-pipeline-bar dash-status-bg-${p.status}`}
                                    style={{ width: `${(p.count / maxCount) * 100}%` }}
                                />
                            </div>
                            <span className="dash-pipeline-count">{p.count}</span>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    renderTrend = (data: MyDashboardDataType) => {
        const { daily, total_sent, total_replies } = data.email_trends
        const { hoveredDayIndex } = this.state
        const sentValues = daily.map(d => d.sent_count)
        const replyValues = daily.map(d => d.reply_count)
        const maxValue = Math.max(1, ...sentValues, ...replyValues)

        const sentPoints = computePoints(sentValues, maxValue)
        const replyPoints = computePoints(replyValues, maxValue)
        const sentLine = pointsToPath(sentPoints)
        const replyLine = pointsToPath(replyPoints)
        const baseline = CHART_H - CHART_PAD_Y
        const sentArea = sentPoints.length
            ? `${sentLine} L${sentPoints[sentPoints.length - 1].x.toFixed(1)},${baseline} L${sentPoints[0].x.toFixed(1)},${baseline} Z`
            : ''

        const labelIndices = daily.length > 2 ? [0, Math.floor((daily.length - 1) / 2), daily.length - 1] : daily.map((_, i) => i)

        const hovered = hoveredDayIndex !== null ? daily[hoveredDayIndex] : null
        const hoveredSentPoint = hoveredDayIndex !== null ? sentPoints[hoveredDayIndex] : null
        const hoveredReplyPoint = hoveredDayIndex !== null ? replyPoints[hoveredDayIndex] : null
        const tooltipLeftPct = hoveredSentPoint ? (hoveredSentPoint.x / CHART_W) * 100 : 0
        const tooltipEdgeClass = tooltipLeftPct < 12 ? 'dash-trend-tooltip-left' : tooltipLeftPct > 88 ? 'dash-trend-tooltip-right' : ''

        return (
            <div className="dash-panel dash-panel-trend">
                <div className="dash-panel-header">
                    <h3 className="dash-panel-title">Email activity</h3>
                    <div className="dash-legend">
                        <span className="dash-legend-item">
                            <span className="dash-legend-dot dash-legend-sent" />
                            Sent <strong>{total_sent}</strong>
                        </span>
                        <span className="dash-legend-item">
                            <span className="dash-legend-dot dash-legend-reply" />
                            Reply emails <strong>{total_replies}</strong>
                        </span>
                    </div>
                </div>

                <div
                    className="dash-trend-hover-area"
                    onMouseMove={(e) => this.handleTrendPointerMove(e.clientX, e.currentTarget.getBoundingClientRect(), daily.length)}
                    onMouseLeave={this.handleTrendPointerLeave}
                    onTouchMove={(e) => {
                        const touch = e.touches[0]
                        if (touch) this.handleTrendPointerMove(touch.clientX, e.currentTarget.getBoundingClientRect(), daily.length)
                    }}
                    onTouchEnd={this.handleTrendPointerLeave}
                >
                    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" className="dash-trend-svg">
                        <defs>
                            <linearGradient id={this.gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.22" />
                                <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path d={sentArea} fill={`url(#${this.gradientId})`} stroke="none" />
                        <path d={replyLine} className="dash-trend-line-reply" fill="none" />
                        <path d={sentLine} className="dash-trend-line-sent" fill="none" />

                        {hoveredSentPoint && (
                            <>
                                <line
                                    x1={hoveredSentPoint.x} y1={CHART_PAD_Y}
                                    x2={hoveredSentPoint.x} y2={baseline}
                                    className="dash-trend-crosshair"
                                />
                                <circle cx={hoveredSentPoint.x} cy={hoveredSentPoint.y} r="4" className="dash-trend-dot dash-trend-dot-sent" />
                                {hoveredReplyPoint && (
                                    <circle cx={hoveredReplyPoint.x} cy={hoveredReplyPoint.y} r="4" className="dash-trend-dot dash-trend-dot-reply" />
                                )}
                            </>
                        )}
                    </svg>

                    {hovered && (
                        <div
                            className={`dash-trend-tooltip ${tooltipEdgeClass}`}
                            style={{ left: `${tooltipLeftPct}%` }}
                        >
                            <div className="dash-trend-tooltip-date">{formatDateShort(hovered.date)}</div>
                            <div className="dash-trend-tooltip-row">
                                <span className="dash-legend-dot dash-legend-sent" />
                                Sent <strong>{hovered.sent_count}</strong>
                            </div>
                            <div className="dash-trend-tooltip-row">
                                <span className="dash-legend-dot dash-legend-reply" />
                                Replies <strong>{hovered.reply_count}</strong>
                            </div>
                        </div>
                    )}
                </div>

                <div className="dash-trend-axis">
                    {labelIndices.map(i => (
                        <span key={i} className="dash-trend-axis-label">{formatDateShort(daily[i].date)}</span>
                    ))}
                </div>
            </div>
        )
    }

    renderTimeline = (data: MyDashboardDataType) => {
        return (
            <div className="dash-panel dash-panel-timeline">
                <h3 className="dash-panel-title">Recent activity</h3>
                {data.recent_activity.length === 0 ? (
                    <div className="dash-empty">No status changes yet.</div>
                ) : (
                    <div className="dash-timeline">
                        {data.recent_activity.map((item, i) => (
                            <div key={i} className="dash-timeline-row">
                                <span className="dash-timeline-dot" />
                                <div className="dash-timeline-content">
                                    <span className="dash-timeline-text">
                                        <strong>{item.company_name || 'Unknown lead'}</strong> moved to{' '}
                                        <span className={`dash-status-pill dash-status-${item.new_status}`}>
                                            {STATUS_LABELS[item.new_status] || item.new_status}
                                        </span>
                                    </span>
                                    <span className="dash-timeline-time">{timeAgo(item.created_at)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { data, workers, selectedUserId, startDate, endDate, loading, error, needsAuth } = this.state
        const isAdmin = controllers().auth.isAdmin

        return (
            <div className="my-dashboard-page">
                <div className="dash-header">
                    <div>
                        <h1 className="dash-title">
                            {isAdmin && selectedUserId && data
                                ? `${data.viewing_user.username || data.viewing_user.email}'s dashboard`
                                : 'My dashboard'}
                        </h1>
                        <span className="dash-subtitle">{formatDateShort(startDate)} – {formatDateShort(endDate)}</span>
                    </div>
                    <div className="dash-toolbar">
                        {isAdmin && (
                            <select className="dash-user-select" value={selectedUserId} onChange={this.handleUserChange}>
                                <option value="">My dashboard</option>
                                {workers.map(w => (
                                    <option key={w.id} value={w.id}>{w.username || w.email}</option>
                                ))}
                            </select>
                        )}
                        <label className="dash-date-field">
                            From
                            <input type="date" value={startDate} max={endDate} onChange={this.handleStartDateChange} />
                        </label>
                        <label className="dash-date-field">
                            To
                            <input type="date" value={endDate} min={startDate} max={todayISO()} onChange={this.handleEndDateChange} />
                        </label>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="dash-banner dash-banner-info">
                        <span>Sign in to view your dashboard.</span>
                        <button className="dash-banner-action" onClick={() => controllers().modals.authModal.show()}>Sign In</button>
                    </div>
                )}

                {!loading && error && (
                    <div className="dash-banner dash-banner-error">
                        <span>{error}</span>
                        <button className="dash-banner-action" onClick={this.loadDashboard}>Retry</button>
                    </div>
                )}

                {loading && <div className="dash-state"><div className="dash-spinner" /></div>}

                {!loading && !error && !needsAuth && data && (
                    <>
                        {this.renderHero(data)}
                        <div className="dash-row dash-row-queue">
                            {this.renderQueue(data)}
                            {this.renderPipeline(data)}
                        </div>
                        <div className="dash-row dash-row-trend">
                            {this.renderTrend(data)}
                            {this.renderTimeline(data)}
                        </div>
                    </>
                )}
            </div>
        )
    }
}

export { MyDashboardPage }